/**
 * Helper functions for various UI components and data processing
 */

import React from 'react';
import ImageListItemBar from '@mui/material/ImageListItemBar';

/**
 * Creates carousel item data from image imports
 * @param {Array} images - Array of imported images
 * @param {Array} titles - Optional array of titles for images
 * @returns {Array} Array of carousel item objects
 */
export const createCarouselItems = (images, titles = []) => {
  return images.map((image, index) => ({
    src: image,
    alt: titles[index] || `Slide ${index + 1}`,
    id: `carousel-item-${index}`,
  }));
};

/**
 * Creates consistent image style for carousels
 * @param {Object} overrides - Style overrides
 * @returns {Object} Style object
 */
export const getCarouselImageStyle = (overrides = {}) => ({
  width: '100%',
  minHeight: 'auto',
  maxHeight: '691px',
  zIndex: '100',
  ...overrides,
});

/**
 * Creates consistent carousel container style
 * @param {Object} overrides - Style overrides
 * @returns {Object} Style object
 */
export const getCarouselContainerStyle = (overrides = {}) => ({
  width: '100%',
  minHeight: 'auto',
  maxHeight: 'auto',
  zIndex: '100',
  ...overrides,
});

/**
 * Validates image imports and removes duplicates
 * @param {Array} images - Array of image imports
 * @returns {Array} Filtered array without duplicates
 */
export const filterUniqueImages = (images) => {
  const seen = new Set();
  return images.filter((image) => {
    if (seen.has(image)) {
      return false;
    }
    seen.add(image);
    return true;
  });
};

/**
 * Creates meaningful alt text for artwork images
 * @param {string} baseName - Base name for the artwork
 * @param {number} index - Image index
 * @param {string} artistName - Artist name
 * @returns {string} Descriptive alt text
 */
export const createArtworkAltText = (
  baseName,
  index,
  artistName = 'Nikoletta Papakonstantinou'
) => {
  return `${baseName} artwork ${index + 1} by ${artistName}`;
};

/**
 * Creates image data objects suitable for ImageGallery component
 * @param {Array} images - Array of imported images
 * @param {Array} altTexts - Array of alt text strings
 * @returns {Array} Array of image objects with src and alt properties
 */
export const createImageData = (images, altTexts = []) => {
  return images.map((image, index) => ({
    src: image,
    alt: altTexts[index] || `Image ${index + 1}`,
    title: altTexts[index] || `Artwork ${index + 1}`,
  }));
};

/**
 * Creates image data with author information for galleries with captions
 * @param {Array} images - Array of imported images
 * @param {string} authorName - Name of the author/artist
 * @param {string} baseName - Base name for the artwork series
 * @returns {Array} Array of image objects with author information
 */
export const createImageDataWithAuthor = (
  images,
  authorName = 'Nikoletta Papakonstantinou',
  baseName = 'Artwork'
) => {
  return images.map((image, index) => ({
    src: image,
    alt: createArtworkAltText(baseName, index, authorName),
    title: `${baseName} ${index + 1}`,
    author: authorName,
  }));
};

/**
 * Creates styled ImageListItemBar component with consistent styling
 * @param {string} title - The title to display
 * @param {string} author - The author name
 * @returns {JSX.Element} Styled ImageListItemBar component
 */
export const createStyledImageListItemBar = (title, author) => {
  return (
    <ImageListItemBar
      title={title}
      subtitle={<span>by: {author}</span>}
      position="below"
      sx={{
        background: 'none',
        '& .MuiImageListItemBar-title': {
          color: '#333',
          fontSize: '0.9rem',
          fontWeight: 600,
        },
        '& .MuiImageListItemBar-subtitle': {
          color: '#666',
          fontSize: '0.8rem',
        },
      }}
    />
  );
};
